import React, { useState } from 'react';
import { Divider, MenuItem, Popover, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import cs from 'dayjs/locale/cs';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { DateSelector } from '@/components';

import { styles } from './styles';
import 'react-day-picker/dist/style.css';

export type TPopoverProps = {
  anchorEl: HTMLElement | null;
  handleFilterClose: () => void;
};

type TDateFilterId =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'customRange';

const DATE_FILTER_LABEL_KEYS: Record<TDateFilterId, string> = {
  today: 'FILTER_DATE.TODAY',
  yesterday: 'FILTER_DATE.YESTERDAY',
  thisWeek: 'FILTER_DATE.THIS_WEEK',
  lastWeek: 'FILTER_DATE.LAST_WEEK',
  thisMonth: 'FILTER_DATE.THIS_MONTH',
  lastMonth: 'FILTER_DATE.LAST_MONTH',
  customRange: 'FILTER_DATE.CUSTOM_RANGE',
};

type TDateFilterListItem = {
  id: TDateFilterId;
  fromDate?: Dayjs;
  toDate?: Dayjs;
};

type TTimeslotTableDatePopoverProps = TPopoverProps & {
  range?: DateRange;
  setRange: (value?: DateRange) => void;
};

// set first day of week to monday
dayjs.locale({
  ...cs,
  weekStart: 1,
});

export const FilterDatePopover = ({
  anchorEl,
  handleFilterClose,
  range,
  setRange,
}: TTimeslotTableDatePopoverProps) => {
  const { t } = useTranslation();
  const [selectedDateFilterId, setSelectedDateFilterId] = useState<string>('');
  const [dateRangeFilterPopover, setDateRangeFilterPopover] = useState<undefined | HTMLElement>(
    undefined,
  );

  const handleDateFilterOwnRangeClick = (event: React.MouseEvent<HTMLElement>) => {
    if (selectedDateFilterId != 'customRange') setRange(undefined);
    setDateRangeFilterPopover(event.currentTarget);
    setSelectedDateFilterId('customRange');
  };

  const handleDateFilterRangeClick = (menuItem: TDateFilterListItem) => {
    setSelectedDateFilterId(menuItem.id);
    setRange({ from: menuItem.fromDate?.toDate(), to: menuItem.toDate?.toDate() });
    handleFilterClose();
  };

  const handleFilterCancel = () => {
    setSelectedDateFilterId('');
    setRange(undefined);
    handleFilterClose();
  };

  const data: TDateFilterListItem[] = [
    {
      id: 'today',
      fromDate: dayjs().startOf('day').locale('cs'),
      toDate: dayjs().endOf('day').locale('cs'),
    },
    {
      id: 'yesterday',
      fromDate: dayjs().subtract(1, 'day').startOf('day').locale('cs'),
      toDate: dayjs().subtract(1, 'day').endOf('day').locale('cs'),
    },
    {
      id: 'thisWeek',
      fromDate: dayjs().startOf('week').locale('cs'),
      toDate: dayjs().endOf('week').locale('cs'),
    },
    {
      id: 'lastWeek',
      fromDate: dayjs().subtract(1, 'week').startOf('week').locale('cs'),
      toDate: dayjs().subtract(1, 'week').endOf('week').locale('cs'),
    },
    {
      id: 'thisMonth',
      fromDate: dayjs().startOf('month').locale('cs'),
      toDate: dayjs().endOf('month').locale('cs'),
    },
    {
      id: 'lastMonth',
      fromDate: dayjs().subtract(1, 'month').startOf('month').locale('cs'),
      toDate: dayjs().subtract(1, 'month').endOf('month').locale('cs'),
    },
  ];

  return (
    <>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={styles.popover}
      >
        {data.map((menuItem) => (
          <MenuItem
            key={menuItem.id}
            onClick={() => handleDateFilterRangeClick(menuItem)}
            sx={selectedDateFilterId === menuItem.id ? styles.selectedItem : {}}
          >
            <Typography variant="body2">{t(DATE_FILTER_LABEL_KEYS[menuItem.id])}</Typography>
          </MenuItem>
        ))}
        <Divider orientation="horizontal" />
        <MenuItem
          sx={[
            { mb: '0.625rem' },
            selectedDateFilterId === 'customRange' && range ? styles.selectedItem : {},
          ]}
          onClick={handleDateFilterOwnRangeClick}
        >
          <Typography variant="body2">{t('FILTER_DATE.CUSTOM_RANGE')}</Typography>
        </MenuItem>
        <Divider orientation="horizontal" />
        <MenuItem sx={[{ mb: '0.625rem' }]} onClick={handleFilterCancel}>
          <Typography variant="body2">{t('FILTER_DATE.CANCEL_FILTER')}</Typography>
        </MenuItem>
      </Popover>
      <DateSelector
        rangeState={[range, setRange]}
        dateRangeFilterPopoverProps={[dateRangeFilterPopover, setDateRangeFilterPopover]}
      />
    </>
  );
};
